interface ProfileAction {
  label: string;
  href: string;
}

interface ExpertisePillar {
  title: string;
  summary: string;
  points: string[];
}

interface AiExpertiseItem {
  title: string;
  description: string;
  methods: string[];
}

interface EvidenceItem {
  label: string;
  title: string;
  context: string;
  outcome: string;
}

interface WorkMode {
  title: string;
  description: string;
  status: string;
}

interface ContactChannel {
  label: string;
  value: string;
  href: string;
  note: string;
}

interface HeroFocusItem {
  title: string;
  detail: string;
}

interface VecuraCapability {
  title: string;
  description: string;
}

interface VecuraProofPoint {
  value: string;
  label: string;
}

interface VecuraPageContent {
  eyebrow: string;
  headline: string;
  intro: string;
  role: string;
  rolePoints: string[];
  pitch: string;
  proofPoints: VecuraProofPoint[];
  capabilities: VecuraCapability[];
  caseStudy: {
    title: string;
    summary: string;
    details: string[];
  };
}

interface PersonalProfile {
  name: string;
  roleLine: string;
  headline: string;
  intro: string;
  email: string;
  linkedinUrl: string;
  primaryAction: ProfileAction;
}

export const profile: PersonalProfile = {
  name: "Timo Rothdeutsch",
  roleLine:
    "Mitgründer & COO bei Vecura | Softwarearchitektur, KI/RAG und operative Verantwortung",
  headline:
    "Ich führe Software-, KI- und Wissenssysteme von der Idee zur belastbaren Anwendung.",
  intro:
    "Ich arbeite an der Grenze von Fachlichkeit, Daten und Technik: Anforderungen klären, Softwarearchitektur zuschneiden, RAG-Konzepte bewerten und Teams so ausrichten, dass aus Umsetzung ein belastbares System wird.",
  email: "timo@rothdeutsch.de",
  linkedinUrl: "https://www.linkedin.com/in/timo-rothdeutsch-454a49234/",
  primaryAction: {
    label: "Kontakt aufnehmen",
    href: "/#contact",
  },
};

export const heroFocusItems: HeroFocusItem[] = [
  {
    title: "Verstehen",
    detail:
      "Fachliche Ziele, Nutzerkontext und technische Realität auf einen gemeinsamen Stand bringen.",
  },
  {
    title: "Entscheiden",
    detail:
      "Prioritäten, Zuständigkeiten und nächste Schritte so klären, dass Teams handeln können.",
  },
  {
    title: "Verbinden",
    detail:
      "Produkt, Fachseite, Geschäftsaufbau und technische Umsetzung in dieselbe Richtung bringen.",
  },
  {
    title: "Absichern",
    detail: "Risiken, Grenzen und Qualitätsfragen früh sichtbar machen.",
  },
];

export const expertisePillars: ExpertisePillar[] = [
  {
    title: "Mitgründer & COO bei Vecura",
    summary:
      "Bei Vecura übersetze ich Markt-, Kunden- und Produktanforderungen in Plattformrichtung, operative Struktur und tragfähige Wachstumsentscheidungen.",
    points: [
      "Produktfokus, Roadmap, Verantwortlichkeiten und Entscheidungswege strukturieren",
      "Pricing, Positionierung und Vertriebsansätze mit Produkt- und Kundenwert verbinden",
      "Strategische Richtung, technische Machbarkeit und operative Klarheit zusammenhalten",
    ],
  },
  {
    title: "Anforderungsanalyse & Softwarearchitektur",
    summary:
      "Ich mache unklare Anforderungen entscheidbar: Systemgrenzen, Datenflüsse, Schnittstellen, Datenmodelle und Risiken werden früh sichtbar.",
    points: [
      "Zielbilder, Systemzuschnitte, Datenmodelle und Migrationspfade als Entscheidungsgrundlage ausarbeiten",
      "Schnittstellen, Rollenmodelle, Integrationen und das Frontend-/Backend-Zusammenspiel mitdenken",
      "Risiken sichtbar machen, bevor sie als technische Schulden im System landen",
    ],
  },
  {
    title: "KI- & RAG-Verantwortung",
    summary:
      "RAG wird wertvoll, wenn Retrieval, Kontext und Quellenbezug fachlich tragen. Genau dort setze ich an.",
    points: [
      "RAG-Strecken für dokumenten- und wissensintensive Fachsysteme konzipieren",
      "Chunking, Embeddings, Metadaten, Ranking und Kontextsteuerung bewerten",
      "Antwortqualität, Quellenbezug und fachliche Grenzen systematisch prüfen",
    ],
  },
  {
    title: "Technische Führung",
    summary:
      "Ich führe technische Entscheidungen mit Entwicklungsverständnis: detailnah genug für Frontend, Backend, Daten, KI/RAG und Qualität, weit genug für Produkt, Betrieb und Verantwortung.",
    points: [
      "Reviews, Leitplanken und Architekturentscheidungen mit Entwicklungsverständnis führen",
      "Hands-on einsteigen, wenn Architektur, Datenmigration oder Schnittstellenklärung davon profitieren",
      "Zwischen Fachbereich, Produkt, Technik und Betrieb übersetzen",
    ],
  },
];

export const aiExpertiseItems: AiExpertiseItem[] = [
  {
    title: "RAG-Architektur",
    description:
      "RAG beginnt bei Dokumenten, Metadaten und Zugriffsregeln. Erst daraus entsteht ein Antwortkontext, der fachlich hält.",
    methods: [
      "Retrieval-Augmented Generation",
      "Dokumentenverarbeitung",
      "Quellenbezug und Grounding",
      "Rollen- und Zugriffskonzepte",
    ],
  },
  {
    title: "Retrieval-Qualität",
    description:
      "Gute Antworten entstehen vor dem Prompt: durch Datenzuschnitt, Suchstrategie, Ranking, Re-Ranking und ehrliche Bewertung der Treffer.",
    methods: [
      "Embedding-Strategien",
      "Chunking und Metadaten",
      "Semantische Suche",
      "Ranking und Relevanzbewertung",
    ],
  },
  {
    title: "Produktionsnahe KI-Systeme",
    description:
      "KI muss Rollen, Schnittstellen, Fehlerfälle, Evaluation, Betrieb und Verantwortlichkeiten aushalten. Sonst bleibt sie eine Demo.",
    methods: [
      "Prompt- und Kontextsteuerung",
      "Evaluationslogik",
      "Fehleranalyse",
      "Architektur- und Betriebsfähigkeit",
    ],
  },
];

export const evidenceItems: EvidenceItem[] = [
  {
    label: "Vecura",
    title: "Vecura als Plattform aufbauen",
    context:
      "Produkt, Roadmap, Pricing, Marktzugang und RAG-Architektur müssen gemeinsam entschieden werden.",
    outcome:
      "Ich verbinde strategische Richtung, technische Machbarkeit und operative Umsetzung.",
  },
  {
    label: "Architektur",
    title: "Anforderungen in Architektur übersetzen",
    context:
      "Fachliche Wünsche werden erst tragfähig, wenn Daten, Schnittstellen, Rollen und Systemgrenzen geklärt sind.",
    outcome:
      "Ich mache daraus Zielbilder, Migrationspfade und umsetzbare Entscheidungen.",
  },
  {
    label: "Führung",
    title: "Technische Entscheidungen früh klären",
    context:
      "Risiken entstehen, wenn Zuständigkeiten, Alternativen und Qualitätsgrenzen erst während der Umsetzung sichtbar werden.",
    outcome: "Ich schaffe Klarheit, bevor Teams bauen.",
  },
  {
    label: "Betrieb",
    title: "Systeme betreibbar denken",
    context:
      "Wartbarkeit entsteht durch saubere Datenstrukturen, Schnittstellen, Tests und klare Betriebsannahmen.",
    outcome:
      "Ich denke Umsetzung, Betrieb und Weiterentwicklung von Anfang an zusammen.",
  },
  {
    label: "KI",
    title: "KI fachlich belastbar machen",
    context:
      "RAG braucht passende Datenbasis, Retrieval-Qualität, Kontextsteuerung und Quellenbezug.",
    outcome:
      "Ich achte darauf, dass Antworten nachvollziehbar, integrierbar und fachlich prüfbar bleiben.",
  },
];

export const workModes: WorkMode[] = [
  {
    title: "Vecura",
    status: "Mitgründer & COO",
    description:
      "Firmenaufbau, Produktfokus, Roadmap, Pricing, Marktzugang, RAG-Plattform und operative Struktur.",
  },
  {
    title: "Leitende Verantwortung",
    status: "Offen für passende Rollen",
    description:
      "Rollen, in denen technische Richtung, Softwarearchitektur und KI-/RAG-Verantwortung zwischen Business und Engineering geführt werden müssen.",
  },
  {
    title: "Selbstständige Projekte",
    status: "Gezielt möglich",
    description:
      "Punktuell für Analyse, Zielarchitektur, Schnittstellen, RAG-Konzeption oder technische Bewertung, wenn der Hebel klar ist.",
  },
];

export const contactChannels: ContactChannel[] = [
  {
    label: "E-Mail",
    value: "timo@rothdeutsch.de",
    href: "mailto:timo@rothdeutsch.de",
    note: "Rollen, Projekte, konkrete Anfragen.",
  },
  {
    label: "LinkedIn",
    value: "Timo Rothdeutsch",
    href: "https://www.linkedin.com/in/timo-rothdeutsch-454a49234/",
    note: "Beruflicher Kontext und Austausch.",
  },
  {
    label: "Vecura",
    value: "vecura.io · hello@vecura.io",
    href: "https://vecura.io",
    note: "Plattform, Demos, Wissensanwendungen.",
  },
];

export const vecuraPage: VecuraPageContent = {
  eyebrow: "Vecura",
  headline:
    "KI für Organisationen, deren Wissen in Dokumenten, Prozessen und Fachkontext steckt.",
  intro:
    "Vecura macht komplexes Unternehmens- und Fachwissen über eine produktionsnahe RAG-Plattform zugänglich. Im Mittelpunkt stehen belastbare Quellen, nachvollziehbare Antworten und Integration in reale Arbeitsprozesse.",
  role: "Als Mitgründer und COO verantworte ich Aufbau, operative Struktur und technische Richtung mit. Dazu gehören Produkt- und Roadmap-Entscheidungen, Pricing, Positionierung sowie die Verbindung von Plattform, Marketing und Vertrieb.",
  rolePoints: [
    "Produkt- und Plattformrichtung aus Markt- und Kundenanforderungen ableiten",
    "Pricing, Positionierung, Roadmap und Vertriebsansätze entscheidbar machen",
    "RAG, Architektur und operative Umsetzung zusammenführen",
    "Prioritäten, Verantwortlichkeiten und nächste Schritte für Aufbau und Wachstum klären",
  ],
  pitch:
    "Vecura bündelt Datenextraktion, semantische Suche, Retrieval, Kontextsteuerung und individuelle Konfiguration. So wird vorhandenes Wissen nutzbar, ohne jedes KI-Projekt bei null zu beginnen.",
  proofPoints: [
    {
      value: "50.000+",
      label: "Seiten technisches Fachwissen in einem produktiven Case",
    },
    {
      value: "68%",
      label: "Zeitersparnis bei fachlicher Recherche im FBS-Case",
    },
    {
      value: "100%",
      label: "Quellenbezug als Grundprinzip für nachvollziehbare Antworten",
    },
  ],
  capabilities: [
    {
      title: "Datenextraktion",
      description:
        "Dokumente, Tabellen, Abbildungen und fachliche Strukturen werden für belastbare KI-Nutzung vorbereitet.",
    },
    {
      title: "Semantisches Retrieval",
      description:
        "Suchqualität entsteht durch Chunking, Embeddings, Metadaten, Ranking, Re-Ranking und passende Kontextauswahl.",
    },
    {
      title: "Quellenbasierte Antworten",
      description:
        "Antworten bleiben überprüfbar, weil sie mit Quellen und fachlichem Kontext verbunden werden.",
    },
    {
      title: "Individuelle Integration",
      description:
        "Vecura funktioniert als eigene Wissensanwendung, White-Label-Lösung oder integrierter Baustein bestehender Prozesse.",
    },
  ],
  caseStudy: {
    title: "FBS und infra.pilot",
    summary:
      "Mit infra.pilot zeigt Vecura, wie eine spezialisierte Wissensanwendung aus fachlichen Dokumenten, Quellenbezug und RAG-Architektur entsteht.",
    details: [
      "Normen, Qualitätsrichtlinien, Handbücher, Regelwerke und Arbeitsblätter werden zentral erschlossen.",
      "Fachfragen können in natürlicher Sprache gestellt und mit Quellenbezug beantwortet werden.",
      "Das System macht sichtbar, wie aus der Plattform schnell eine fachlich zugeschnittene Anwendung wird.",
    ],
  },
};
